/* eslint-disable */

const INITIALISED_CLASSNAME = 'cmp-bignumbers__number--initialised'
const NO_ANIMATION_CLASSNAME = 'cmp-bignumbers__number--no-animation'
const COMPLETED_CLASSNAME = 'cmp-bignumbers__number--completed'
const COUNT_SELECTOR = '.cmp-bignumbers__count'
const FRAME_RATE = 60 /* Frames Per Second, typical for RequestAnimationFrame */
export const BIG_NUMBERS_SELECTOR = `.cmp-bignumbers__number`

interface BigNumberConfig {
  startNumber: number
  endNumber: number
  duration: number
}

interface numberType extends Element {
  modelInstance?: BigNumber
}

export class BigNumber {
  private _element: HTMLElement
  private config: BigNumberConfig
  private frames: number = 0
  private frameAdvance: number = 0
  private count: HTMLSpanElement
  private countValue: number = 0
  private thousandSeparator: string = ','

  constructor(element: HTMLElement) {
    this._element = element as HTMLElement
    this.count = this._element.querySelector(COUNT_SELECTOR)
    this.config = Object.assign({}, JSON.parse(this._element.dataset.config))

    // Store the model instance on the element for the observer
    // @ts-ignore
    this._element.modelInstance = this

    // Determine thousand separator based on current path
    this.thousandSeparator = this.getThousandSeparator()

    console.log('BigNumbers - Path:', window.location.pathname, 'Separator:', this.thousandSeparator)

    if (isNaN(this.config.endNumber) || isNaN(this.config.startNumber) || isNaN(this.config.duration)) return

    // Immediately format the initial value
    this.formatInitialValue()

    if (this.prefersReducedMotion()) {
      this._element.classList.add(NO_ANIMATION_CLASSNAME)
      return this
    }

    this.initialise()
    return this
  }

  private getThousandSeparator(): string {
    const path = window.location.pathname.toLowerCase()

    // Check for period separator paths
    if (path.includes('/language-masters/pt_br') ||
        path.includes('/language-masters/es') ||
        path.includes('/br/pt') ||
        path.includes('/es/es')) {
      return '.'
    }

    // Default to comma for all other paths
    return ','
  }

  private formatNumber(number: number): string {
    const numStr = Math.round(number).toString()
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandSeparator)
  }

  private formatInitialValue(): void {
    if (!this.count) return

    // Get the raw number from the config (most reliable source)
    const rawNumber = this.config.endNumber
    if (!isNaN(rawNumber)) {
      this.count.textContent = this.formatNumber(rawNumber)
      console.log('BigNumbers - Formatted initial value:', this.count.textContent)
    }
  }

  prefersReducedMotion(): boolean {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    return mediaQuery.matches
  }

  initialise() {
    // Set up animation parameters
    if (this.config.startNumber !== this.config.endNumber) {
      // Only set countValue and call displayCount if we need animation
      this.countValue = this.config.startNumber
      this.displayCount()
    } else {
      // If start equals end, just set count value but don't overwrite the formatted display
      this.countValue = this.config.endNumber
    }

    this.frames = Math.floor(FRAME_RATE * this.config.duration)
    this.frameAdvance = (this.config.endNumber - this.config.startNumber) / this.frames

    this._element.classList.add(INITIALISED_CLASSNAME)
  }

  displayCount() {
    if (this.count) {
      this.count.textContent = this.formatNumber(this.countValue)
    }
  }

  getElement(): HTMLElement {
    return this._element
  }

  countUp() {
    if (this.countValue >= this.config.endNumber) return

    if (Math.round(this.countValue + this.frameAdvance) >= this.config.endNumber) {
      this.countValue = this.config.endNumber
      this.displayCount()
      this._element.classList.add(COMPLETED_CLASSNAME)
      return
    }

    this.countValue += this.frameAdvance
    this.displayCount()
    return window.requestAnimationFrame(() => this.countUp())
  }

  startCount() {
    window.requestAnimationFrame(() => this.countUp())
  }

  resetCount() {
    if (this.countValue === this.config.endNumber) {
      this._element.classList.remove(COMPLETED_CLASSNAME, NO_ANIMATION_CLASSNAME)
      this.countValue = this.config.startNumber
      this.initialise()
    }
  }
}

export const BigNumbersObserver = (components: Array<Element>) => {
  if (!components || components.length <= 0) return

  console.log('BigNumbers - Setting up observer for', components.length, 'components')

  if ('IntersectionObserver' in window) {
    const bigNumbersObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach(entry => {
          const number: numberType = entry.target

          if (entry.isIntersecting) {
            console.log('BigNumbers - Component entered viewport, starting animation')
            if (!number.classList.contains(COMPLETED_CLASSNAME) &&
                !number.classList.contains(NO_ANIMATION_CLASSNAME) &&
                number.modelInstance) {
              number.modelInstance.startCount()
            }
          } else {
            console.log('BigNumbers - Component left viewport, resetting')
            if (number.modelInstance) {
              number.modelInstance.resetCount()
            }
          }
        })
      },
      { threshold: 0.9 },
    )

    components.forEach((component: Element) => {
      console.log('BigNumbers - Observing component:', component)
      bigNumbersObserver.observe(component)
    })
  }
}

/* eslint-enable */